// Planner → Taken (tasks) — FRONTEND-BLUEPRINT §3.3; PLAN-W30. The last persona placeholder. Lists the
// tenant's tasks (optionally tied to an enabled regime activity), with an assignee + due date + a small
// status lifecycle. Create form picks an activity (enabled regime) + assignee (tenant users); per-row the
// status can be changed and a task removed. Gated activity_task.read / .write / .update / .delete.
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ListTodo, Plus, Trash2 } from "lucide-react";
import { ApiError, TENANT_PATHS } from "@sympllex/api-client";
import { api } from "../../lib/api";
import { Can } from "../../lib/rbac";
import { useI18n } from "../../lib/i18n";
import { PageHeader } from "../../components/page-header";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DataTable, type Column } from "@/components/data-table";

const errText = (e: unknown) => (e instanceof ApiError ? `Server ${e.status}: ${JSON.stringify(e.body)}` : (e as Error).message);
const STATUSES = ["open", "in_progress", "done", "cancelled"] as const;
const STATUS_VARIANT: Record<string, "default" | "secondary" | "outline"> = { open: "secondary", in_progress: "default", done: "outline", cancelled: "outline" };

interface Task { id: string; title: string; status: string; dueDate: string | null; activityName: string | null; activityCode: string | null; assigneeEmail: string | null; assigneeName: string | null }
interface ActOpt { orgActivityConfigId: string; activityName: string; activityCode: string | null }
interface UserOpt { userId: string; email: string; fullName?: string | null }

export function TakenRoute() {
  const { t } = useI18n();
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: ["tasks"] });
  const { data, isLoading, error } = useQuery({ queryKey: ["tasks"], queryFn: () => api.listTasks() as Promise<Task[]> });

  const columns: Column<Task>[] = [
    { key: "title", header: t("taken.taskTitle"), render: (r) => <span className="font-medium">{String(r.title)}</span> },
    { key: "activityName", header: t("taken.activity"), render: (r) => r.activityName ? `${r.activityCode ? r.activityCode + " " : ""}${r.activityName}` : "—" },
    { key: "assigneeEmail", header: t("taken.assignee"), render: (r) => r.assigneeName || r.assigneeEmail || "—" },
    { key: "dueDate", header: t("taken.due"), render: (r) => (r.dueDate ? new Date(r.dueDate).toLocaleDateString() : "—") },
    { key: "status", header: t("taken.status"), render: (r) => <StatusCell id={r.id} status={r.status} onDone={invalidate} /> },
    { key: "id", header: "", render: (r) => (<Can perm="activity_task.delete"><RemoveButton id={r.id} onDone={invalidate} /></Can>) },
  ];

  return (
    <div className="flex flex-col gap-6">
      <PageHeader title={t("taken.title")} description={t("taken.sub")} />
      <Can perm="activity_task.write"><AddTaskCard onAdded={invalidate} /></Can>
      {isLoading && <p className="text-sm text-muted-foreground">{t("common.loading")}</p>}
      {error && <p className="text-sm text-destructive">{errText(error)}</p>}
      {data && (data.length
        ? <DataTable rows={data as unknown as Record<string, unknown>[]} columns={columns as unknown as Column<Record<string, unknown>>[]} getRowId={(r) => String(r.id)} />
        : <p className="text-sm text-muted-foreground">{t("taken.empty")}</p>)}
    </div>
  );
}

function StatusCell({ id, status, onDone }: { id: string; status: string; onDone: () => void }) {
  const { t } = useI18n();
  const mut = useMutation({ mutationFn: (s: string) => api.updateEntity(TENANT_PATHS.tasks, id, { status: s }), onSuccess: onDone });
  return (
    <Can perm="activity_task.update" fallback={<Badge variant={STATUS_VARIANT[status] ?? "secondary"}>{t(`taken.st.${status}`)}</Badge>}>
      <Select value={status} onValueChange={(s) => mut.mutate(s)}>
        <SelectTrigger className="h-7 w-36"><SelectValue /></SelectTrigger>
        <SelectContent>{STATUSES.map((s) => (<SelectItem key={s} value={s}>{t(`taken.st.${s}`)}</SelectItem>))}</SelectContent>
      </Select>
    </Can>
  );
}

function RemoveButton({ id, onDone }: { id: string; onDone: () => void }) {
  const { t } = useI18n();
  const mut = useMutation({ mutationFn: () => api.deactivateEntity(TENANT_PATHS.tasks, id), onSuccess: onDone });
  return (
    <button type="button" className="inline-flex items-center gap-1 text-xs text-destructive hover:underline" onClick={() => mut.mutate()} disabled={mut.isPending}>
      <Trash2 className="size-3.5" aria-hidden />{t("crud.remove")}
    </button>
  );
}

function AddTaskCard({ onAdded }: { onAdded: () => void }) {
  const { t } = useI18n();
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [activity, setActivity] = useState("");
  const [assignee, setAssignee] = useState("");
  const [due, setDue] = useState("");
  const activities = useQuery({ queryKey: ["task-activities"], queryFn: () => api.listTaskActivities() as Promise<ActOpt[]> });
  const assignees = useQuery({ queryKey: ["task-assignees"], queryFn: () => api.listTaskAssignees() as Promise<UserOpt[]> });
  const add = useMutation({
    mutationFn: () => api.createTask({
      title: title.trim(),
      ...(description.trim() ? { description: description.trim() } : {}),
      ...(activity ? { orgActivityConfigId: activity } : {}),
      ...(assignee ? { assignedToUserId: assignee } : {}),
      ...(due ? { dueDate: due } : {}),
    }),
    onSuccess: () => { setTitle(""); setDescription(""); setActivity(""); setAssignee(""); setDue(""); onAdded(); },
  });

  return (
    <Card className="max-w-2xl">
      <CardHeader><CardTitle className="flex items-center gap-2"><Plus className="size-4 text-primary" aria-hidden />{t("taken.new")}</CardTitle></CardHeader>
      <CardContent>
        <form className="flex flex-col gap-3" onSubmit={(e) => { e.preventDefault(); add.mutate(); }}>
          <div className="grid gap-1.5">
            <Label htmlFor="tk-title">{t("taken.taskTitle")}</Label>
            <Input id="tk-title" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={300} required />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="tk-desc">{t("taken.description")}</Label>
            <Textarea id="tk-desc" value={description} onChange={(e) => setDescription(e.target.value)} rows={2} />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="grid gap-1.5">
              <Label htmlFor="tk-act">{t("taken.activity")}</Label>
              <Select value={activity} onValueChange={setActivity}>
                <SelectTrigger id="tk-act"><SelectValue placeholder={t("taken.pickActivity")} /></SelectTrigger>
                <SelectContent>{(activities.data ?? []).map((a) => (<SelectItem key={a.orgActivityConfigId} value={a.orgActivityConfigId}>{a.activityCode ? a.activityCode + " " : ""}{a.activityName}</SelectItem>))}</SelectContent>
              </Select>
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="tk-assignee">{t("taken.assignee")}</Label>
              <Select value={assignee} onValueChange={setAssignee}>
                <SelectTrigger id="tk-assignee"><SelectValue placeholder={t("taken.pickAssignee")} /></SelectTrigger>
                <SelectContent>{(assignees.data ?? []).map((u) => (<SelectItem key={u.userId} value={u.userId}>{u.fullName || u.email}</SelectItem>))}</SelectContent>
              </Select>
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="tk-due">{t("taken.due")}</Label>
              <Input id="tk-due" type="date" value={due} onChange={(e) => setDue(e.target.value)} />
            </div>
          </div>
          <div>
            <Button type="submit" disabled={add.isPending || !title.trim()}>{add.isPending ? t("crud.saving") : t("taken.new")}</Button>
            {add.isError && <span className="ml-3 text-sm text-destructive">{errText(add.error)}</span>}
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
